import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, gql, useLazyQuery } from "@apollo/client";
import DaumPostcodeEmbed from "react-daum-postcode";
import CustomMapMarker2 from "../../../components/common/custom-map-marker-2";

const GET_BRANDS = gql`
  query MissionAdminBrands {
    missionAdminBrands {
      id
      name
      lat
      lng
      roadAddr
      instagramUrl
      naverPlaceUrl
      createdAt
      updatedAt
    }
  }
`;

const UPSERT_BRAND = gql`
  mutation MissionAdminUpsertBrand($name: String!) {
    missionAdminUpsertBrand(name: $name) {
      id
      name
    }
  }
`;

const UPDATE_BRAND = gql`
  mutation MissionAdminUpdateBrand(
    $brandId: Int!
    $input: MissionAdminUpdateBrandInput!
  ) {
    missionAdminUpdateBrand(brandId: $brandId, input: $input) {
      id
      name
      lat
      lng
      roadAddr
      instagramUrl
      naverPlaceUrl
    }
  }
`;

const DELETE_BRAND = gql`
  mutation MissionAdminDeleteBrand($brandId: Int!) {
    missionAdminDeleteBrand(brandId: $brandId) {
      ok
      error
    }
  }
`;

const CREATE_BRAND_ACCOUNT = gql`
  mutation MissionAdminCreateBrandAccount($input: MissionAdminCreateBrandAccountInput!) {
    missionAdminCreateBrandAccount(input: $input) {
      id
      email
      name
      status
      createdAt
    }
  }
`;

const RESET_BRAND_ACCOUNT_PASSWORD = gql`
  mutation MissionAdminResetBrandAccountPassword($email: String!) {
    missionAdminResetBrandAccountPassword(email: $email) {
      ok
      error
      tempPassword
      account {
        id
        email
        name
      }
    }
  }
`;

const GET_COORDS = gql`
  query GetCoords($address: String!) {
    getCoords(address: $address) {
      lat
      lng
    }
  }
`;

interface Brand {
  id: number | null;
  name: string;
  lat: number | null;
  lng: number | null;
  roadAddr: string | null;
  instagramUrl: string | null;
  naverPlaceUrl: string | null;
  createdAt?: string;
}

interface AccountFormData {
  email: string;
  password: string;
  name: string;
  brandId: number | null;
  role: string;
}

const MissionAdminBrands = () => {
  const [editingBrand, setEditingBrand] = useState<Brand | null>(null);
  const [openPostModal, setOpenPostModal] = useState(false);
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);
  const [lat, setLat] = useState<number | null>(null);
  const [lng, setLng] = useState<number | null>(null);
  const [roadAddr, setRoadAddr] = useState<string>("");
  const [instagramUrl, setInstagramUrl] = useState<string>("");
  const [naverPlaceUrl, setNaverPlaceUrl] = useState<string>("");
  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
  const [resetPasswordEmail, setResetPasswordEmail] = useState("");
  const [resetPasswordResult, setResetPasswordResult] = useState<{
    tempPassword: string;
    account: { email: string; name: string | null };
  } | null>(null);
  const [accountFormData, setAccountFormData] = useState<AccountFormData>({
    email: "",
    password: "",
    name: "",
    brandId: null,
    role: "OWNER",
  });

  const { data, loading, error, refetch } = useQuery(GET_BRANDS);
  const [getCoordsFromAddress] = useLazyQuery(GET_COORDS);

  const [upsertBrand, { loading: saving }] = useMutation(UPSERT_BRAND, {
    onCompleted: () => {
      setEditingBrand(null);
      refetch();
    },
  });

  const [updateBrand, { loading: updating }] = useMutation(UPDATE_BRAND, {
    onCompleted: () => {
      setEditingBrand(null);
      refetch();
    },
  });

  const [deleteBrand, { loading: deleting }] = useMutation(DELETE_BRAND, {
    onCompleted: () => {
      refetch();
    },
  });

  const [createBrandAccount, { loading: creatingAccount }] = useMutation(
    CREATE_BRAND_ACCOUNT,
    {
      onCompleted: () => {
        setIsAccountModalOpen(false);
        setAccountFormData({
          email: "",
          password: "",
          name: "",
          brandId: null,
          role: "OWNER",
        });
        refetch();
        alert("브랜드 계정이 생성되었습니다.");
      },
      onError: (error) => {
        alert(`계정 생성 실패: ${error.message}`);
      },
    }
  );

  const [resetBrandAccountPassword, { loading: resettingPassword }] = useMutation(
    RESET_BRAND_ACCOUNT_PASSWORD,
    {
      onCompleted: (data) => {
        if (data.missionAdminResetBrandAccountPassword.ok) {
          setResetPasswordResult({
            tempPassword: data.missionAdminResetBrandAccountPassword.tempPassword,
            account: data.missionAdminResetBrandAccountPassword.account,
          });
        } else {
          alert(`비밀번호 초기화 실패: ${data.missionAdminResetBrandAccountPassword.error}`);
        }
      },
      onError: (error) => {
        alert(`비밀번호 초기화 실패: ${error.message}`);
      },
    }
  );

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!editingBrand) {
      return;
    }

    if (!editingBrand.name || editingBrand.name.trim() === "") {
      alert("브랜드명을 입력해주세요.");
      return;
    }

    try {
      if (editingBrand.id) {
        // 수정
        await updateBrand({
          variables: {
            brandId: editingBrand.id,
            input: {
              name: editingBrand.name.trim(),
              lat: lat !== null ? lat : undefined,
              lng: lng !== null ? lng : undefined,
              roadAddr: roadAddr || undefined,
              instagramUrl: instagramUrl || undefined,
              naverPlaceUrl: naverPlaceUrl || undefined,
            },
          },
        });
        alert("브랜드가 수정되었습니다.");
        setEditingBrand(null);
        setLat(null);
        setLng(null);
        setRoadAddr("");
        setInstagramUrl("");
        setNaverPlaceUrl("");
      } else {
        // 생성 - 브랜드 생성 후 최초 계정 생성
        const brandResult = await upsertBrand({
          variables: {
            name: editingBrand.name.trim(),
          },
        });

        const brandId = brandResult.data?.missionAdminUpsertBrand?.id;

        if (brandId && accountFormData.email && accountFormData.password) {
          // 최초 계정 생성
          try {
            await createBrandAccount({
              variables: {
                input: {
                  email: accountFormData.email.trim(),
                  password: accountFormData.password,
                  name: accountFormData.name.trim() || null,
                  brandId: brandId,
                  role: accountFormData.role,
                },
              },
            });
            alert("브랜드와 최초 계정이 생성되었습니다.");
          } catch (accountError) {
            alert(`브랜드는 생성되었지만 계정 생성에 실패했습니다: ${accountError instanceof Error ? accountError.message : "알 수 없는 에러"}`);
          }
        } else {
          alert("브랜드가 생성되었습니다.");
        }

        setEditingBrand(null);
        setAccountFormData({
          email: "",
          password: "",
          name: "",
          brandId: null,
          role: "OWNER",
        });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "알 수 없는 에러가 발생했습니다.";
      alert(`에러: ${errorMessage}`);
    }
  };

  const handleAddressComplete = (data: any) => {
    const fullAddress = data.address;
    setRoadAddr(fullAddress);
    setOpenPostModal(false);

    getCoordsFromAddress({
      variables: {
        address: fullAddress,
      },
    }).then((response) => {
      setLng(response.data.getCoords.lng);
      setLat(response.data.getCoords.lat);
    });
  };

  const handleAddressClick = () => {
    setRoadAddr("");
    setLng(null);
    setLat(null);
    setOpenPostModal(true);
  };

  const handleMapSelect = (selectedLng: number, selectedLat: number) => {
    setLng(selectedLng);
    setLat(selectedLat);
    setIsMapModalOpen(false);
  };

  const handleEditBrand = (brand: Brand) => {
    setEditingBrand(brand);
    setLat(brand.lat || null);
    setLng(brand.lng || null);
    setRoadAddr(brand.roadAddr || "");
    setInstagramUrl(brand.instagramUrl || "");
    setNaverPlaceUrl(brand.naverPlaceUrl || "");
  };

  const handleNewBrand = () => {
    setEditingBrand({
      id: null,
      name: "",
      lat: null,
      lng: null,
      roadAddr: null,
      instagramUrl: null,
      naverPlaceUrl: null,
    });
    setLat(null);
    setLng(null);
    setRoadAddr("");
    setInstagramUrl("");
    setNaverPlaceUrl("");
    setAccountFormData({
      email: "",
      password: "",
      name: "",
      brandId: null,
      role: "OWNER",
    });
  };

  const handleCancel = () => {
    setEditingBrand(null);
    setLat(null);
    setLng(null);
    setRoadAddr("");
    setInstagramUrl("");
    setNaverPlaceUrl("");
    setOpenPostModal(false);
    setIsMapModalOpen(false);
    setAccountFormData({
      email: "",
      password: "",
      name: "",
      brandId: null,
      role: "OWNER",
    });
  };

  const handleOpenAccountModal = (brandId: number) => {
    setAccountFormData({
      email: "",
      password: "",
      name: "",
      brandId: brandId,
      role: "OWNER",
    });
    setIsAccountModalOpen(true);
  };

  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!accountFormData.email || !accountFormData.password) {
      alert("아이디와 비밀번호를 입력해주세요.");
      return;
    }

    if (!accountFormData.brandId) {
      alert("브랜드를 선택해주세요.");
      return;
    }

    try {
      await createBrandAccount({
        variables: {
          input: {
            email: accountFormData.email.trim(),
            password: accountFormData.password,
            name: accountFormData.name.trim() || null,
            brandId: accountFormData.brandId,
            role: accountFormData.role,
          },
        },
      });
    } catch (error) {
      console.error("Account creation error:", error);
    }
  };

  const handleDelete = async (brandId: number, brandName: string) => {
    if (
      !window.confirm(
        `정말 "${brandName}" 브랜드를 삭제하시겠습니까?\n연결된 캠페인이 있으면 삭제할 수 없습니다.`
      )
    ) {
      return;
    }

    try {
      const { data } = await deleteBrand({
        variables: { brandId },
      });

      if (data.missionAdminDeleteBrand.ok) {
        alert("브랜드가 삭제되었습니다.");
      } else {
        alert(`삭제 실패: ${data.missionAdminDeleteBrand.error}`);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "알 수 없는 에러가 발생했습니다.";
      alert(`에러: ${errorMessage}`);
    }
  };

  if (loading) return <div>로딩 중...</div>;
  if (error) return <div>에러: {error.message}</div>;

  const brands = data?.missionAdminBrands || [];

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetPasswordEmail.trim()) {
      alert("이메일을 입력해주세요.");
      return;
    }

    try {
      await resetBrandAccountPassword({
        variables: {
          email: resetPasswordEmail.trim(),
        },
      });
    } catch (error) {
      console.error("Password reset error:", error);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">브랜드 관리</h1>
        <button
          onClick={() => {
            setIsResetPasswordModalOpen(true);
            setResetPasswordEmail("");
            setResetPasswordResult(null);
          }}
          className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
        >
          비밀번호 초기화
        </button>
      </div>

      {/* 브랜드 목록 */}
      <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                브랜드명
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                생성일
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                액션
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {brands.map((brand: Brand) => (
              <tr key={brand.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {brand.id}
                </td>
                <td className="px-6 py-4 text-sm font-medium">{brand.name}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {brand.createdAt ? new Date(brand.createdAt).toLocaleDateString("ko-KR") : "-"}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditBrand(brand)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      수정
                    </button>
                    {brand.id && (
                      <>
                        <button
                          onClick={() => handleOpenAccountModal(brand.id!)}
                          className="text-green-600 hover:text-green-900"
                        >
                          계정 생성
                        </button>
                        <button
                          onClick={() => handleDelete(brand.id!, brand.name)}
                          disabled={deleting}
                          className="text-red-600 hover:text-red-900 disabled:opacity-50"
                        >
                          삭제
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {brands.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            등록된 브랜드가 없습니다.
          </div>
        )}
      </div>

      {/* 새 브랜드 추가 버튼 */}
      <div className="mb-6">
        <button
          onClick={handleNewBrand}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          새 브랜드 추가
        </button>
      </div>

      {/* 편집 모달 */}
      {editingBrand && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-semibold mb-4">
              {editingBrand.id ? "브랜드 수정" : "브랜드 생성"}
            </h2>
            <form onSubmit={handleSave}>
              <div className="space-y-4 max-h-[80vh] overflow-y-auto">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    브랜드명 *
                  </label>
                  <input
                    type="text"
                    required
                    value={editingBrand.name || ""}
                    onChange={(e) =>
                      setEditingBrand({
                        ...editingBrand,
                        name: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="브랜드명을 입력하세요"
                  />
                </div>

                {/* 위치 정보 섹션 */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    도로명 주소 (선택)
                  </label>
                  <div className="w-full px-3 py-2 border border-gray-300 rounded-md">
                    {roadAddr ? (
                      <div className="flex justify-between items-center">
                        {roadAddr}
                        <div
                          onClick={handleAddressClick}
                          className="text-gray-700 border border-gray-300 rounded py-0.5 px-2 bg-gray-100 cursor-pointer text-sm"
                        >
                          주소 변경하기
                        </div>
                      </div>
                    ) : (
                      <span
                        onClick={handleAddressClick}
                        className="text-blue-600 cursor-pointer"
                      >
                        주소를 선택해주세요
                      </span>
                    )}
                  </div>

                  {openPostModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
                      <div className="bg-white p-4 rounded-xl w-full max-w-xl">
                        <DaumPostcodeEmbed
                          onComplete={handleAddressComplete}
                          scriptUrl="//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js"
                          className="w-full h-96"
                        />
                        <button
                          onClick={() => setOpenPostModal(false)}
                          className="w-full mt-4 py-2 bg-gray-200 rounded-lg"
                        >
                          닫기
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* 지도에서 위치 선택 */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    지도에서 위치 선택 (선택)
                  </label>
                  <div className="text-gray-600 text-xs mb-2">
                    지도를 움직여 정확한 위치를 선택해주세요.
                  </div>
                  <div className="w-full px-3 py-2 border border-gray-300 rounded-md">
                    {lat !== null && lng !== null ? (
                      <div className="flex justify-between items-center">
                        <div className="text-green-500">위치 설정 완료</div>
                        <div
                          onClick={() => {
                            if (lat === null || lng === null) {
                              setLat(37.5665);
                              setLng(126.9780);
                            }
                            setIsMapModalOpen(true);
                          }}
                          className="text-gray-700 border border-gray-300 rounded py-0.5 px-2 bg-gray-100 cursor-pointer text-sm"
                        >
                          다시 설정하기
                        </div>
                      </div>
                    ) : (
                      <div
                        onClick={() => {
                          if (lat === null || lng === null) {
                            setLat(37.5665);
                            setLng(126.9780);
                          }
                          setIsMapModalOpen(true);
                        }}
                        className="text-blue-600 cursor-pointer"
                      >
                        설정하기
                      </div>
                    )}
                  </div>
                </div>

                {/* 인스타그램 URL */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    인스타그램 URL (선택)
                  </label>
                  <input
                    type="url"
                    value={instagramUrl}
                    onChange={(e) => setInstagramUrl(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="https://instagram.com/..."
                  />
                </div>

                {/* 네이버 플레이스 URL */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    네이버 플레이스 URL (선택)
                  </label>
                  <input
                    type="url"
                    value={naverPlaceUrl}
                    onChange={(e) => setNaverPlaceUrl(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="https://place.naver.com/..."
                  />
                </div>

                {/* 브랜드 생성 시에만 최초 계정 생성 필드 표시 */}
                {!editingBrand.id && (
                  <div className="border-t pt-4 mt-4">
                    <h3 className="text-sm font-medium text-gray-700 mb-3">
                      최초 계정 정보
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          아이디 *
                        </label>
                        <input
                          type="text"
                          required={!editingBrand.id}
                          value={accountFormData.email}
                          onChange={(e) =>
                            setAccountFormData({
                              ...accountFormData,
                              email: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          placeholder="아이디를 입력하세요"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          비밀번호 *
                        </label>
                        <input
                          type="password"
                          required={!editingBrand.id}
                          value={accountFormData.password}
                          onChange={(e) =>
                            setAccountFormData({
                              ...accountFormData,
                              password: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          placeholder="비밀번호를 입력하세요"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          이름 (선택)
                        </label>
                        <input
                          type="text"
                          value={accountFormData.name}
                          onChange={(e) =>
                            setAccountFormData({
                              ...accountFormData,
                              name: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          placeholder="이름을 입력하세요"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          역할 *
                        </label>
                        <select
                          value={accountFormData.role}
                          onChange={(e) =>
                            setAccountFormData({
                              ...accountFormData,
                              role: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        >
                          <option value="OWNER">OWNER</option>
                          <option value="ADMIN">ADMIN</option>
                          <option value="EDITOR">EDITOR</option>
                          <option value="VIEWER">VIEWER</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-2 mt-6">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                >
                  취소
                </button>
                <button
                  type="submit"
                  disabled={saving || updating}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving || updating ? "저장 중..." : "저장"}
                </button>
              </div>
            </form>

            {/* 지도 모달 */}
            {isMapModalOpen && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
                <div className="bg-white py-6 rounded-xl shadow-lg w-[80%] max-w-2xl">
                  <h2 className="text-xl font-medium mb-4 px-6">
                    상세 위치 선택
                  </h2>
                  <Map
                    onSelectAddress={handleMapSelect}
                    lng={lng ?? 126.9780}
                    lat={lat ?? 37.5665}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 브랜드 계정 생성 모달 */}
      {isAccountModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-semibold mb-4">브랜드 계정 생성</h2>
            <form onSubmit={handleCreateAccount}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    아이디 *
                  </label>
                  <input
                    type="text"
                    required
                    value={accountFormData.email}
                    onChange={(e) =>
                      setAccountFormData({
                        ...accountFormData,
                        email: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="아이디를 입력하세요"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    비밀번호 *
                  </label>
                  <input
                    type="password"
                    required
                    value={accountFormData.password}
                    onChange={(e) =>
                      setAccountFormData({
                        ...accountFormData,
                        password: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="비밀번호를 입력하세요"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    이름 (선택)
                  </label>
                  <input
                    type="text"
                    value={accountFormData.name}
                    onChange={(e) =>
                      setAccountFormData({
                        ...accountFormData,
                        name: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="이름을 입력하세요"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    역할 *
                  </label>
                  <select
                    value={accountFormData.role}
                    onChange={(e) =>
                      setAccountFormData({
                        ...accountFormData,
                        role: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="OWNER">OWNER</option>
                    <option value="ADMIN">ADMIN</option>
                    <option value="EDITOR">EDITOR</option>
                    <option value="VIEWER">VIEWER</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end space-x-2 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setIsAccountModalOpen(false);
                    setAccountFormData({
                      email: "",
                      password: "",
                      name: "",
                      brandId: null,
                      role: "OWNER",
                    });
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                >
                  취소
                </button>
                <button
                  type="submit"
                  disabled={creatingAccount}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                >
                  {creatingAccount ? "생성 중..." : "계정 생성"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 비밀번호 초기화 모달 */}
      {isResetPasswordModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold mb-4">브랜드 계정 비밀번호 초기화</h2>
            
            {resetPasswordResult ? (
              <div>
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-gray-600 mb-2">비밀번호가 초기화되었습니다.</p>
                  <p className="text-sm font-medium mb-1">계정: {resetPasswordResult.account.email}</p>
                  {resetPasswordResult.account.name && (
                    <p className="text-sm text-gray-600 mb-3">이름: {resetPasswordResult.account.name}</p>
                  )}
                  <div className="mt-3 p-3 bg-white border-2 border-blue-300 rounded">
                    <p className="text-xs text-gray-500 mb-1">임시 비밀번호:</p>
                    <p className="text-lg font-mono font-bold text-blue-600 break-all">
                      {resetPasswordResult.tempPassword}
                    </p>
                  </div>
                  <p className="text-xs text-red-600 mt-2">
                    ⚠️ 이 비밀번호를 안전하게 보관하고 사용자에게 전달해주세요.
                  </p>
                </div>
                <button
                  onClick={() => {
                    setIsResetPasswordModalOpen(false);
                    setResetPasswordEmail("");
                    setResetPasswordResult(null);
                  }}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  확인
                </button>
              </div>
            ) : (
              <form onSubmit={handleResetPassword}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    이메일 *
                  </label>
                  <input
                    type="email"
                    required
                    value={resetPasswordEmail}
                    onChange={(e) => setResetPasswordEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="비밀번호를 초기화할 계정의 이메일을 입력하세요"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setIsResetPasswordModalOpen(false);
                      setResetPasswordEmail("");
                      setResetPasswordResult(null);
                    }}
                    className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    disabled={resettingPassword}
                    className="flex-1 px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50"
                  >
                    {resettingPassword ? "처리 중..." : "비밀번호 초기화"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Map = ({
  onSelectAddress,
  lat,
  lng,
}: {
  onSelectAddress: (lng: number, lat: number) => void;
  lng: number;
  lat: number;
}) => {
  const mapElement = useRef<HTMLDivElement | null>(null);
  const { naver } = window;
  let map: naver.maps.Map;
  const [newMap, setNewMap] = useState<naver.maps.Map | null>(null);

  const center = new naver.maps.LatLng(lat, lng);

  useEffect(() => {
    if (!mapElement.current || !naver) {
      return;
    }

    const mapOptions: naver.maps.MapOptions = {
      center,
      zoom: 15,
      minZoom: 7,
      maxZoom: 19,
      zoomControl: true,
      zoomControlOptions: {
        style: naver.maps.ZoomControlStyle.SMALL,
        position: naver.maps.Position.TOP_RIGHT,
      },
      mapDataControl: false,
      scaleControl: false,
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    map = new naver.maps.Map(mapElement.current, mapOptions);
    setNewMap(map);
    const marker = new naver.maps.Marker({
      position: center,
      map,
      clickable: true,
      icon: {
        content: CustomMapMarker2(),
        anchor: new naver.maps.Point(25, 58),
      },
    });

    naver.maps.Event.addListener(map, "drag", function (e) {
      const centerCoord = map.getCenter();
      marker.setPosition(centerCoord);
    });
  }, []);

  function onClickClose() {
    const center = newMap?.getCenter();
    const lat = center?.y as number;
    const lng = center?.x as number;
    onSelectAddress(lng, lat);
  }

  return (
    <div className="w-[100%] h-[400px] px-6">
      <div
        ref={mapElement}
        id="map"
        style={{ width: "100%", height: 400 }}
      ></div>
      <button
        onClick={onClickClose}
        className="mt-4 w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
      >
        완료
      </button>
    </div>
  );
};

export default MissionAdminBrands;
